import { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { Plus, Search, User, Users, Phone, Mail, FileText, Trash2, Edit, Building2, Calendar, AlertTriangle, Upload, File, Download, Briefcase, Home, Archive, Eye, Bed, ArrowUp, ArrowDown, ArrowUpDown, Ban, Send, Loader2 } from "lucide-react";
import api from "../lib/api";
import { format, differenceInDays, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { useAuth } from "../context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function GuestsPage() {
  const [guests, setGuests] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [roomDetailOpen, setRoomDetailOpen] = useState(false);
  const [leistungDetailOpen, setLeistungDetailOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [selectedGuestForArchive, setSelectedGuestForArchive] = useState(null);
  const [selectedGuestForRoom, setSelectedGuestForRoom] = useState(null);
  const [selectedGuestForLeistung, setSelectedGuestForLeistung] = useState(null);
  const [selectedGuestForDocs, setSelectedGuestForDocs] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [editingGuest, setEditingGuest] = useState(null);
  const [activeTab, setActiveTab] = useState("personal");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  // Blacklist Dialog
  const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false);
  const [selectedGuestForBlacklist, setSelectedGuestForBlacklist] = useState(null);
  const [blacklistReason, setBlacklistReason] = useState("");
  const [blacklistNotes, setBlacklistNotes] = useState("");
  const [addingToBlacklist, setAddingToBlacklist] = useState(false);
  // Email Dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedGuestForEmail, setSelectedGuestForEmail] = useState(null);
  const [emailType, setEmailType] = useState("invoice"); // invoice, reminder1, reminder2, reminder3
  const [sendingEmail, setSendingEmail] = useState(false);
  // Email Ay/Yıl seçimi
  const [emailMonth, setEmailMonth] = useState(new Date().getMonth() + 1);
  const [emailYear, setEmailYear] = useState(new Date().getFullYear());
  // PDF Preview
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [docFormData, setDocFormData] = useState({
    document_type: "sonstige",
    title: "",
    description: "",
  });
  const [formData, setFormData] = useState({
    anrede: "",
    bewohner_type: "sozial",
    first_name: "",
    last_name: "",
    date_of_birth: "",
    nationality: "",
    email: "",
    phone: "",
    id_number: "",
    id_type: "",
    bg_number: "",
    // Partner/Evli
    has_partner: false,
    partner_id: "",
    // Schlüsselkaution
    schluessel_kaution: "",
    schluessel_kaution_bezahlt: false,
    schluessel_kaution_datum: "",
    // Jobcenter
    jobcenter_name: "",
    jobcenter_city: "",
    jobcenter_email: "",
    jobcenter_address: "",
    aktenzeichen: "",
    sachbearbeiter_name: "",
    sachbearbeiter_phone: "",
    sachbearbeiter_email: "",
    leistung_start_date: "",
    leistung_end_date: "",
    // Privat
    arbeitgeber: "",
    einkommen: "",
    notes: "",
  });

  useEffect(() => {
    fetchGuests();
    fetchRooms();
  }, []);

  const fetchGuests = async () => {
    try {
      const response = await api.get("/guests");
      setGuests(response.data);
    } catch (error) {
      console.error("Error fetching guests:", error);
      toast.error("Bewohner konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await api.get("/rooms");
      setRooms(response.data);
    } catch (error) {
      console.error("Error fetching rooms:", error);
    }
  };

  const getGuestRoomInfo = (guest) => {
    if (!guest.current_room_id) return null;
    const room = rooms.find(r => r.id === guest.current_room_id);
    const bed = room?.beds?.find(b => b.id === guest.current_bed_id);
    return { room, bed };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      anrede: "",
      bewohner_type: "sozial",
      first_name: "",
      last_name: "",
      date_of_birth: "",
      nationality: "",
      email: "",
      phone: "",
      id_number: "",
      id_type: "",
      bg_number: "",
      has_partner: false,
      partner_id: "",
      schluessel_kaution: "",
      schluessel_kaution_bezahlt: false,
      schluessel_kaution_datum: "",
      jobcenter_name: "",
      jobcenter_city: "",
      jobcenter_email: "",
      jobcenter_address: "",
      aktenzeichen: "",
      sachbearbeiter_name: "",
      sachbearbeiter_phone: "",
      sachbearbeiter_email: "",
      leistung_start_date: "",
      leistung_end_date: "",
      arbeitgeber: "",
      einkommen: "",
      notes: "",
    });
    setEditingGuest(null);
    setActiveTab("personal");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Boş stringleri null'a çevir (email validation için)
      const cleanData = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value === '' || value === undefined) {
          cleanData[key] = null;
        } else {
          cleanData[key] = value;
        }
      });
      
      const submitData = {
        ...cleanData,
        einkommen: formData.einkommen ? parseFloat(formData.einkommen) : null,
        schluessel_kaution: formData.schluessel_kaution ? parseFloat(formData.schluessel_kaution) : null,
      };
      
      if (editingGuest) {
        await api.put(`/guests/${editingGuest.id}`, submitData);
        toast.success("Bewohner erfolgreich aktualisiert");
      } else {
        await api.post("/guests", submitData);
        toast.success("Bewohner erfolgreich hinzugefügt");
      }
      setDialogOpen(false);
      resetForm();
      fetchGuests();
    } catch (error) {
      console.error("Error saving guest:", error);
      // Handle validation errors from Pydantic
      let errorMessage = "Fehler beim Speichern";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation error array
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleEdit = (guest) => {
    setEditingGuest(guest);
    setFormData({
      anrede: guest.anrede || "",
      bewohner_type: guest.bewohner_type || "sozial",
      first_name: guest.first_name || "",
      last_name: guest.last_name || "",
      date_of_birth: guest.date_of_birth || "",
      nationality: guest.nationality || "",
      email: guest.email || "",
      phone: guest.phone || "",
      id_number: guest.id_number || "",
      id_type: guest.id_type || "",
      bg_number: guest.bg_number || "",
      has_partner: guest.has_partner || false,
      partner_id: guest.partner_id || "",
      schluessel_kaution: guest.schluessel_kaution || "",
      schluessel_kaution_bezahlt: guest.schluessel_kaution_bezahlt || false,
      schluessel_kaution_datum: guest.schluessel_kaution_datum || "",
      jobcenter_name: guest.jobcenter_name || "",
      jobcenter_city: guest.jobcenter_city || "",
      jobcenter_email: guest.jobcenter_email || "",
      jobcenter_address: guest.jobcenter_address || "",
      aktenzeichen: guest.aktenzeichen || "",
      sachbearbeiter_name: guest.sachbearbeiter_name || "",
      sachbearbeiter_phone: guest.sachbearbeiter_phone || "",
      sachbearbeiter_email: guest.sachbearbeiter_email || "",
      leistung_start_date: guest.leistung_start_date || "",
      leistung_end_date: guest.leistung_end_date || "",
      arbeitgeber: guest.arbeitgeber || "",
      einkommen: guest.einkommen || "",
      notes: guest.notes || "",
    });
    setDialogOpen(true);
  };

  const openArchiveDialog = (guest) => {
    setSelectedGuestForArchive(guest);
    setArchiveDialogOpen(true);
  };

  const handleArchive = async () => {
    if (!selectedGuestForArchive) return;
    try {
      await api.patch(`/guests/${selectedGuestForArchive.id}`, { archived: true });
      toast.success("Bewohner wurde archiviert");
      setArchiveDialogOpen(false);
      setSelectedGuestForArchive(null);
      fetchGuests();
    } catch (error) {
      console.error("Error archiving guest:", error);
      toast.error("Fehler beim Archivieren");
    }
  };

  const handleRestore = async (guestId) => {
    try {
      await api.patch(`/guests/${guestId}`, { archived: false });
      toast.success("Bewohner wurde wiederhergestellt");
      fetchGuests();
    } catch (error) {
      console.error("Error restoring guest:", error);
      toast.error("Fehler beim Wiederherstellen");
    }
  };

  // Blacklist fonksiyonları
  const openBlacklistDialog = (guest) => {
    setSelectedGuestForBlacklist(guest);
    setBlacklistReason("");
    setBlacklistNotes("");
    setBlacklistDialogOpen(true);
  };

  const handleAddToBlacklist = async () => {
    if (!selectedGuestForBlacklist || !blacklistReason) return;
    
    setAddingToBlacklist(true);
    try {
      await api.post("/blacklist", {
        first_name: selectedGuestForBlacklist.first_name,
        last_name: selectedGuestForBlacklist.last_name,
        reason: blacklistReason,
        notes: blacklistNotes,
        blocked_date: format(new Date(), "yyyy-MM-dd")
      });
      toast.success("Zur Blacklist hinzugefügt");
      setBlacklistDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hinzufügen");
    } finally {
      setAddingToBlacklist(false);
    }
  };

  // Email fonksiyonları
  const openEmailDialog = (guest) => {
    setSelectedGuestForEmail(guest);
    setEmailType("invoice");
    // Mevcut ay/yıl ile başlat
    const now = new Date();
    setEmailMonth(now.getMonth() + 1);
    setEmailYear(now.getFullYear());
    setPreviewPdf(null);
    setEmailDialogOpen(true);
  };

  const handlePreviewInvoice = async () => {
    if (!selectedGuestForEmail) return;
    
    setPreviewLoading(true);
    try {
      const response = await api.post(`/guests/${selectedGuestForEmail.id}/document-preview`, {
        document_type: emailType,
        month: emailMonth,
        year: emailYear
      });
      setPreviewPdf(response.data.pdf_base64);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Vorschau konnte nicht geladen werden");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedGuestForEmail) return;
    
    setSendingEmail(true);
    try {
      await api.post(`/guests/${selectedGuestForEmail.id}/send-email`, {
        email_type: emailType,
        month: emailMonth,
        year: emailYear
      });
      toast.success("E-Mail wurde gesendet");
      setEmailDialogOpen(false);
      setPreviewPdf(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDelete = async (guestId) => {
    if (!window.confirm("Möchten Sie diesen Bewohner ENDGÜLTIG löschen? Alle zugehörigen Daten (Zahlungen, Buchungen, Dokumente) werden ebenfalls gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.")) return;
    try {
      // permanent=true für dauerhafte Löschung (nur in Archiv-Ansicht)
      await api.delete(`/guests/${guestId}?permanent=true`);
      toast.success("Bewohner und alle zugehörigen Daten wurden dauerhaft gelöscht");
      fetchGuests();
    } catch (error) {
      console.error("Error deleting guest:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const openRoomDetail = (guest) => {
    setSelectedGuestForRoom(guest);
    setRoomDetailOpen(true);
  };

  const openLeistungDetail = (guest) => {
    setSelectedGuestForLeistung(guest);
    setLeistungDetailOpen(true);
  };

  // Document functions
  const openDocumentDialog = async (guest) => {
    setSelectedGuestForDocs(guest);
    setDocumentDialogOpen(true);
    try {
      const response = await api.get(`/documents/guest/${guest.id}`);
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Datei ist zu groß. Maximum 10MB.");
      return;
    }
    
    setUploadingDoc(true);
    
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        await api.post("/documents", {
          guest_id: selectedGuestForDocs.id,
          document_type: docFormData.document_type,
          title: docFormData.title || file.name,
          description: docFormData.description,
          file_name: file.name,
          file_data: base64,
        });
        
        toast.success("Dokument erfolgreich hochgeladen");
        
        const response = await api.get(`/documents/guest/${selectedGuestForDocs.id}`);
        setDocuments(response.data);
        
        setDocFormData({
          document_type: "sonstige",
          title: "",
          description: "",
        });
        
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error("Fehler beim Hochladen");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDownloadDocument = async (docId, fileName) => {
    try {
      const response = await api.get(`/documents/${docId}`);
      const base64 = response.data.file_data;
      
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${base64}`;
      link.download = fileName;
      link.click();
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Fehler beim Herunterladen");
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Möchten Sie dieses Dokument wirklich löschen?")) return;
    try {
      await api.delete(`/documents/${docId}`);
      toast.success("Dokument erfolgreich gelöscht");
      const response = await api.get(`/documents/guest/${selectedGuestForDocs.id}`);
      setDocuments(response.data);
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const getDocumentTypeLabel = (type) => {
    const types = {
      kimlik: "Ausweis/Pass",
      leistungsbescheid: "Leistungsbescheid",
      mietvertrag: "Mietvertrag",
      sonstige: "Sonstige",
    };
    return types[type] || type;
  };

  const getDocumentTypeColor = (type) => {
    const colors = {
      kimlik: "bg-blue-100 text-blue-800",
      leistungsbescheid: "bg-amber-100 text-amber-800",
      mietvertrag: "bg-emerald-100 text-emerald-800",
      sonstige: "bg-slate-100 text-slate-800",
    };
    return colors[type] || "bg-slate-100 text-slate-800";
  };

  const getLeistungStatus = (endDate) => {
    if (!endDate) return null;
    const today = new Date();
    const end = parseISO(endDate);
    const daysRemaining = differenceInDays(end, today);
    
    if (daysRemaining < 0) {
      return { status: "expired", label: "Abgelaufen", class: "status-overdue" };
    } else if (daysRemaining <= 14) {
      return { status: "critical", label: `${daysRemaining} Tage`, class: "status-overdue" };
    } else if (daysRemaining <= 30) {
      return { status: "warning", label: `${daysRemaining} Tage`, class: "status-pending" };
    } else {
      return { status: "ok", label: `${daysRemaining} Tage`, class: "status-paid" };
    }
  };

  const filteredGuests = guests.filter((guest) => {
    // Arşiv filtresi
    const isArchived = guest.archived === true;
    if (showArchived !== isArchived) return false;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      guest.first_name?.toLowerCase().includes(searchLower) ||
      guest.last_name?.toLowerCase().includes(searchLower) ||
      guest.email?.toLowerCase().includes(searchLower) ||
      guest.phone?.toLowerCase().includes(searchLower) ||
      guest.aktenzeichen?.toLowerCase().includes(searchLower) ||
      guest.jobcenter_name?.toLowerCase().includes(searchLower)
    );
  });

  // Sıralama fonksiyonu
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sıralanmış liste
  const sortedGuests = [...filteredGuests].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aValue, bValue;
    
    switch (sortConfig.key) {
      case 'name':
        // Ad + Soyadı ile sırala (Almanca alfabetik)
        aValue = `${(a.first_name || '').trim()} ${(a.last_name || '').trim()}`.toLowerCase().trim();
        bValue = `${(b.first_name || '').trim()} ${(b.last_name || '').trim()}`.toLowerCase().trim();
        break;
      case 'typ':
        // Sozial önce, Privat sonra
        aValue = a.bewohner_type === 'privat' ? 1 : 0;
        bValue = b.bewohner_type === 'privat' ? 1 : 0;
        break;
      case 'phone':
        aValue = (a.phone || '').toLowerCase();
        bValue = (b.phone || '').toLowerCase();
        break;
      case 'jobcenter':
        aValue = (a.jobcenter_name || a.arbeitgeber || '').toLowerCase();
        bValue = (b.jobcenter_name || b.arbeitgeber || '').toLowerCase();
        break;
      case 'leistung_end':
        // Tarihe göre sırala - tarih olmayanlar sona
        aValue = a.leistung_end_date ? new Date(a.leistung_end_date).getTime() : (sortConfig.direction === 'asc' ? Infinity : -Infinity);
        bValue = b.leistung_end_date ? new Date(b.leistung_end_date).getTime() : (sortConfig.direction === 'asc' ? Infinity : -Infinity);
        break;
      default:
        return 0;
    }
    
    // String karşılaştırma
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.localeCompare(bValue, 'de');
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    }
    
    // Sayı karşılaştırma
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Sıralama ikonu
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-3 w-3 text-slate-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3 text-slate-700" />
      : <ArrowDown className="h-3 w-3 text-slate-700" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="guests-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bewohner
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Verwalten Sie Bewohnerdaten und Leistungsübernahmen
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="add-guest-btn">
              <Plus className="h-4 w-4 mr-2" />
              Neuer Bewohner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingGuest ? "Bewohner bearbeiten" : "Neuen Bewohner hinzufügen"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Bewohner Type Selection - First Step */}
              {!editingGuest && (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <Label className="text-base font-medium mb-3 block">Art des Mietverhältnisses</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div 
                      className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                        formData.bewohner_type === "sozial" 
                          ? "border-blue-500 bg-blue-50" 
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                      onClick={() => setFormData((prev) => ({ ...prev, bewohner_type: "sozial" }))}
                      data-testid="bewohner-type-sozial"
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        formData.bewohner_type === "sozial" ? "border-blue-500" : "border-slate-300"
                      }`}>
                        {formData.bewohner_type === "sozial" && (
                          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="cursor-pointer font-medium flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-blue-600" />
                          Sozial (Jobcenter)
                        </span>
                        <p className="text-xs text-slate-500 mt-1">
                          Miete wird vom Jobcenter übernommen
                        </p>
                      </div>
                    </div>
                    <div 
                      className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                        formData.bewohner_type === "privat" 
                          ? "border-emerald-500 bg-emerald-50" 
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                      onClick={() => setFormData((prev) => ({ ...prev, bewohner_type: "privat" }))}
                      data-testid="bewohner-type-privat"
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        formData.bewohner_type === "privat" ? "border-emerald-500" : "border-slate-300"
                      }`}>
                        {formData.bewohner_type === "privat" && (
                          <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="cursor-pointer font-medium flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-emerald-600" />
                          Privat (Selbstzahler)
                        </span>
                        <p className="text-xs text-slate-500 mt-1">
                          Miete wird selbst bezahlt
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {editingGuest && (
                <div className="flex items-center gap-2 p-2 bg-slate-100 rounded-lg">
                  {formData.bewohner_type === "sozial" ? (
                    <>
                      <Building2 className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium">Sozial (Jobcenter)</span>
                    </>
                  ) : (
                    <>
                      <Briefcase className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm font-medium">Privat (Selbstzahler)</span>
                    </>
                  )}
                </div>
              )}

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className={`grid w-full ${formData.bewohner_type === "sozial" ? "grid-cols-4" : "grid-cols-4"}`}>
                  <TabsTrigger value="personal" data-testid="tab-personal">Persönlich</TabsTrigger>
                  {formData.bewohner_type === "sozial" ? (
                    <TabsTrigger value="jobcenter" data-testid="tab-jobcenter">Jobcenter</TabsTrigger>
                  ) : (
                    <TabsTrigger value="arbeit" data-testid="tab-arbeit">Arbeit/Einkommen</TabsTrigger>
                  )}
                  <TabsTrigger value="notes" data-testid="tab-notes">Notizen</TabsTrigger>
                </TabsList>

                {/* Persönliche Daten Tab */}
                <TabsContent value="personal" className="space-y-4 mt-4">
                  {/* Anrede, Vorname, Nachname - Aynı satırda */}
                  <div className="flex gap-3">
                  <div className="space-y-2 w-24 flex-shrink-0">
                    <Label className="flex items-center gap-1 text-sm">
                      Anrede <span className="text-red-500">*</span>
                    </Label>
                    <Select value={formData.anrede || ""} onValueChange={(val) => setFormData(prev => ({ ...prev, anrede: val }))}>
                      <SelectTrigger data-testid="guest-anrede" className={`h-9 ${!formData.anrede ? "border-red-300" : ""}`}>
                        <SelectValue placeholder="..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Herr">Herr</SelectItem>
                        <SelectItem value="Frau">Frau</SelectItem>
                        <SelectItem value="Divers">Divers</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 flex-1">
                    <Label htmlFor="first_name" className="flex items-center gap-1 text-sm">
                      Vorname <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      className={`h-9 ${!formData.first_name ? "border-red-300" : ""}`}
                      data-testid="guest-first-name"
                    />
                  </div>
                  <div className="space-y-2 flex-1">
                    <Label htmlFor="last_name" className="flex items-center gap-1 text-sm">
                      Nachname <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      className={`h-9 ${!formData.last_name ? "border-red-300" : ""}`}
                      data-testid="guest-last-name"
                    />
                  </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="date_of_birth">Geburtsdatum</Label>
                      <Input
                        id="date_of_birth"
                        name="date_of_birth"
                        type="date"
                        value={formData.date_of_birth}
                        onChange={handleInputChange}
                        data-testid="guest-date-of-birth"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nationality">Staatsangehörigkeit</Label>
                      <Input
                        id="nationality"
                        name="nationality"
                        value={formData.nationality}
                        onChange={handleInputChange}
                        placeholder="z.B. Deutsch, Türkisch"
                        data-testid="guest-nationality"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        data-testid="guest-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        data-testid="guest-phone"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        Ausweistyp <span className="text-red-500">*</span>
                      </Label>
                      <Select 
                        value={formData.id_type || ""} 
                        onValueChange={(val) => setFormData(prev => ({ ...prev, id_type: val }))}
                      >
                        <SelectTrigger 
                          data-testid="guest-id-type"
                          className={!formData.id_type ? "border-red-300" : ""}
                        >
                          <SelectValue placeholder="Bitte wählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Ausweis">Ausweis</SelectItem>
                          <SelectItem value="Reisepass">Reisepass</SelectItem>
                          <SelectItem value="Fiktionsbescheinigung">Fiktionsbescheinigung</SelectItem>
                          <SelectItem value="Aufenthaltstitel">Aufenthaltstitel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="id_number" className="flex items-center gap-1">
                        Ausweisnummer <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="id_number"
                        name="id_number"
                        value={formData.id_number}
                        onChange={handleInputChange}
                        required
                        className={!formData.id_number ? "border-red-300" : ""}
                        data-testid="guest-id-number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bg_number" className="flex items-center gap-1">
                        BG-Nummer <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="bg_number"
                        name="bg_number"
                        value={formData.bg_number}
                        onChange={handleInputChange}
                        required
                        className={!formData.bg_number ? "border-red-300" : ""}
                        data-testid="guest-bg-number"
                      />
                    </div>
                  </div>

                  {/* Partner & Kaution - Aynı satırda */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Partner / Ehepartner Bölümü - Lila ton */}
                    <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="h-4 w-4 text-violet-600" />
                        <span className="font-semibold text-violet-800">Partner / Ehepartner</span>
                      </div>
                      <p className="text-xs text-violet-600 mb-3">
                        Gemeinsame Abrechnung der Miete
                      </p>
                      <div className="space-y-2">
                        <Select 
                          value={formData.partner_id || "none"} 
                          onValueChange={(val) => setFormData(prev => ({ 
                            ...prev, 
                            partner_id: val === "none" ? "" : val,
                            has_partner: val !== "none"
                          }))}
                        >
                          <SelectTrigger className="bg-white border-violet-300">
                            <SelectValue placeholder="Kein Partner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Kein Partner</SelectItem>
                            {guests
                              .filter(g => g.id !== editingGuest?.id && !g.archived)
                              .sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`, 'de'))
                              .map(g => (
                                <SelectItem key={g.id} value={g.id}>
                                  {g.last_name}, {g.first_name}
                                </SelectItem>
                              ))
                            }
                          </SelectContent>
                        </Select>
                        {formData.partner_id && formData.partner_id !== "none" && (
                          <div className="p-2 bg-violet-100 rounded text-xs text-violet-700 mt-2">
                            Miete wird gemeinsam abgerechnet (z.B. 870€)
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Schlüsselkaution Bölümü */}
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                        </svg>
                        Schlüsselkaution
                      </h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label htmlFor="schluessel_kaution" className="text-xs">Betrag (€)</Label>
                            <Input
                              id="schluessel_kaution"
                              name="schluessel_kaution"
                              type="number"
                              step="0.01"
                              placeholder="50.00"
                              value={formData.schluessel_kaution}
                              onChange={handleInputChange}
                              className="h-9"
                              data-testid="guest-schluessel-kaution"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="schluessel_kaution_datum" className="text-xs">Bezahlt am</Label>
                            <Input
                              id="schluessel_kaution_datum"
                              name="schluessel_kaution_datum"
                              type="date"
                              value={formData.schluessel_kaution_datum}
                              onChange={handleInputChange}
                              className="h-9"
                              data-testid="guest-schluessel-datum"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="schluessel_kaution_bezahlt"
                            name="schluessel_kaution_bezahlt"
                            checked={formData.schluessel_kaution_bezahlt}
                            onChange={(e) => setFormData({...formData, schluessel_kaution_bezahlt: e.target.checked})}
                            className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                          />
                          <Label htmlFor="schluessel_kaution_bezahlt" className="text-sm font-medium text-amber-800">
                            Bezahlt
                          </Label>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Jobcenter Tab (for sozial) */}
                {formData.bewohner_type === "sozial" && (
                  <TabsContent value="jobcenter" className="space-y-4 mt-4">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                      <h4 className="font-medium text-blue-800 mb-1">Leistungsübernahme</h4>
                      <p className="text-sm text-blue-700">
                        Jobcenter übernimmt die Miete für max. 6 Monate. Bitte das Enddatum beachten!
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="jobcenter_name">Jobcenter Name</Label>
                        <Input
                          id="jobcenter_name"
                          name="jobcenter_name"
                          placeholder="z.B. Jobcenter Nürnberg"
                          value={formData.jobcenter_name}
                          onChange={handleInputChange}
                          data-testid="guest-jobcenter-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="jobcenter_city">Jobcenter Stadt</Label>
                        <Input
                          id="jobcenter_city"
                          name="jobcenter_city"
                          value={formData.jobcenter_city}
                          onChange={handleInputChange}
                          data-testid="guest-jobcenter-city"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="jobcenter_email">Jobcenter E-Mail (für Rechnungen)</Label>
                        <Input
                          id="jobcenter_email"
                          name="jobcenter_email"
                          type="email"
                          value={formData.jobcenter_email}
                          onChange={handleInputChange}
                          placeholder="rechnungen@jobcenter.de"
                          data-testid="guest-jobcenter-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="jobcenter_address">Jobcenter Adresse</Label>
                        <Input
                          id="jobcenter_address"
                          name="jobcenter_address"
                          value={formData.jobcenter_address}
                          onChange={handleInputChange}
                          placeholder="Straße, PLZ Ort"
                          data-testid="guest-jobcenter-address"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="aktenzeichen">Aktenzeichen</Label>
                      <Input
                        id="aktenzeichen"
                        name="aktenzeichen"
                        placeholder="z.B. 12345BG0001234"
                        value={formData.aktenzeichen}
                        onChange={handleInputChange}
                        className="font-mono"
                        data-testid="guest-aktenzeichen"
                      />
                    </div>
                    
                    <div className="border-t border-slate-200 pt-4 mt-4">
                      <h4 className="font-medium text-slate-700 mb-3">Sachbearbeiter</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="sachbearbeiter_name">Name</Label>
                          <Input
                            id="sachbearbeiter_name"
                            name="sachbearbeiter_name"
                            value={formData.sachbearbeiter_name}
                            onChange={handleInputChange}
                            data-testid="guest-sachbearbeiter-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="sachbearbeiter_phone">Telefon</Label>
                          <Input
                            id="sachbearbeiter_phone"
                            name="sachbearbeiter_phone"
                            value={formData.sachbearbeiter_phone}
                            onChange={handleInputChange}
                            data-testid="guest-sachbearbeiter-phone"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="sachbearbeiter_email">E-Mail</Label>
                          <Input
                            id="sachbearbeiter_email"
                            name="sachbearbeiter_email"
                            type="email"
                            value={formData.sachbearbeiter_email}
                            onChange={handleInputChange}
                            data-testid="guest-sachbearbeiter-email"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-slate-200 pt-4 mt-4">
                      <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Leistungszeitraum
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="leistung_start_date">Beginn der Leistung</Label>
                          <Input
                            id="leistung_start_date"
                            name="leistung_start_date"
                            type="date"
                            value={formData.leistung_start_date}
                            onChange={handleInputChange}
                            data-testid="guest-leistung-start"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="leistung_end_date">Ende der Leistung</Label>
                          <Input
                            id="leistung_end_date"
                            name="leistung_end_date"
                            type="date"
                            value={formData.leistung_end_date}
                            onChange={handleInputChange}
                            className="border-amber-300 focus:ring-amber-500"
                            data-testid="guest-leistung-end"
                          />
                          <p className="text-xs text-amber-600">
                            30 Tage vorher wird eine Erinnerung angezeigt
                          </p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                )}

                {/* Arbeit/Einkommen Tab (for privat) */}
                {formData.bewohner_type === "privat" && (
                  <TabsContent value="arbeit" className="space-y-4 mt-4">
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg mb-4">
                      <h4 className="font-medium text-emerald-800 mb-1">Privater Mieter</h4>
                      <p className="text-sm text-emerald-700">
                        Dieser Bewohner zahlt die Miete selbst. Bitte Einkommensnachweise prüfen.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="arbeitgeber">Arbeitgeber</Label>
                        <Input
                          id="arbeitgeber"
                          name="arbeitgeber"
                          placeholder="z.B. Firma GmbH"
                          value={formData.arbeitgeber}
                          onChange={handleInputChange}
                          data-testid="guest-arbeitgeber"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="einkommen">Monatliches Einkommen (€)</Label>
                        <Input
                          id="einkommen"
                          name="einkommen"
                          type="number"
                          step="0.01"
                          placeholder="z.B. 2500"
                          value={formData.einkommen}
                          onChange={handleInputChange}
                          data-testid="guest-einkommen"
                        />
                      </div>
                    </div>
                  </TabsContent>
                )}

                {/* Notizen Tab */}
                <TabsContent value="notes" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notizen</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={6}
                      placeholder="Zusätzliche Informationen zum Bewohner..."
                      data-testid="guest-notes"
                    />
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Abbrechen
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-guest-btn">
                  {editingGuest ? "Aktualisieren" : "Speichern"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Archive Toggle */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="guest-search"
          />
        </div>
        <Button
          variant={showArchived ? "default" : "outline"}
          onClick={() => setShowArchived(!showArchived)}
          className={showArchived ? "bg-amber-600 hover:bg-amber-700" : ""}
          data-testid="toggle-archived"
        >
          <Archive className="h-4 w-4 mr-2" />
          {showArchived ? "Archivierte anzeigen" : "Archiv"}
        </Button>
      </div>

      {/* Guests List - Mobile Cards / Desktop Table */}
      <Card className="border-slate-200">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {showArchived ? "Archivierte Bewohner" : "Bewohnerliste"} ({sortedGuests.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          {/* Mobile/Tablet View - Cards */}
          <div className="block lg:hidden space-y-3">
            {sortedGuests.length > 0 ? (
              sortedGuests.map((guest) => {
                const leistungStatus = getLeistungStatus(guest.leistung_end_date);
                const isSozial = guest.bewohner_type !== "privat";
                const roomInfo = getGuestRoomInfo(guest);
                return (
                  <div 
                    key={guest.id} 
                    className={`p-3 rounded-lg border ${showArchived ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}
                    data-testid={`guest-card-${guest.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {guest.first_name} {guest.last_name}
                        </div>
                        {guest.phone && (
                          <div className="text-xs text-slate-500 mt-0.5">{guest.phone}</div>
                        )}
                      </div>
                      {isSozial ? (
                        <Badge className="bg-blue-100 text-blue-800 text-xs flex-shrink-0">Sozial</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-800 text-xs flex-shrink-0">Privat</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {guest.current_bed_id ? (
                        <Badge 
                          className="status-occupied text-xs cursor-pointer hover:opacity-80" 
                          onClick={() => openRoomDetail(guest)}
                        >
                          <Bed className="h-3 w-3 mr-1" />
                          {roomInfo?.room?.room_number || 'Untergebracht'}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Frei</Badge>
                      )}
                      {isSozial && leistungStatus && (
                        <Badge 
                          className={`${leistungStatus.class} text-xs cursor-pointer hover:opacity-80`}
                          onClick={() => openLeistungDetail(guest)}
                        >
                          {leistungStatus.status === "critical" && <AlertTriangle className="h-3 w-3 mr-1" />}
                          {leistungStatus.label}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-slate-200">
                      <Button variant="ghost" size="sm" onClick={() => openDocumentDialog(guest)}>
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(guest)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      {showArchived ? (
                        <>
                          <Button variant="ghost" size="sm" className="text-emerald-600" onClick={() => handleRestore(guest.id)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(guest.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          {user?.role === "admin" && (
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => openBlacklistDialog(guest)} title="Zur Blacklist">
                              <Ban className="h-4 w-4" />
                            </Button>
                          )}
                          {guest.email && (
                            <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => openEmailDialog(guest)} title="E-Mail senden">
                              <Send className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="text-amber-600" onClick={() => openArchiveDialog(guest)} title="Archivieren">
                            <Archive className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-slate-500">
                <User className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm">{showArchived ? "Keine archivierten Bewohner" : "Keine Bewohner gefunden"}</p>
              </div>
            )}
          </div>

          {/* Desktop View - Table */}
          <div className="hidden lg:block rounded-md border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className={showArchived ? "bg-amber-50" : "bg-slate-50"}>
                  <TableHead 
                    className="font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      <SortIcon columnKey="name" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('typ')}
                  >
                    <div className="flex items-center gap-1">
                      Typ
                      <SortIcon columnKey="typ" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('phone')}
                  >
                    <div className="flex items-center gap-1">
                      Kontakt
                      <SortIcon columnKey="phone" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('jobcenter')}
                  >
                    <div className="flex items-center gap-1">
                      Jobcenter/Arbeitgeber
                      <SortIcon columnKey="jobcenter" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('leistung_end')}
                  >
                    <div className="flex items-center gap-1">
                      Leistung bis
                      <SortIcon columnKey="leistung_end" />
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold text-slate-700">Status</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedGuests.length > 0 ? (
                  sortedGuests.map((guest) => {
                    const leistungStatus = getLeistungStatus(guest.leistung_end_date);
                    const isSozial = guest.bewohner_type !== "privat";
                    const roomInfo = getGuestRoomInfo(guest);
                    return (
                      <TableRow key={guest.id} className={`hover:bg-slate-50/50 ${showArchived ? 'bg-amber-50/30' : ''}`} data-testid={`guest-row-${guest.id}`}>
                        <TableCell>
                          <div className="font-medium text-slate-900">
                            {guest.first_name} {guest.last_name}
                          </div>
                          {guest.aktenzeichen && (
                            <div className="text-xs text-slate-500 font-mono mt-0.5">
                              {guest.aktenzeichen}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {isSozial ? (
                            <Badge className="bg-blue-100 text-blue-800">
                              <Building2 className="h-3 w-3 mr-1" />
                              Sozial
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-800">
                              <Briefcase className="h-3 w-3 mr-1" />
                              Privat
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {guest.phone && (
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Phone className="h-3 w-3" />
                                {guest.phone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {isSozial && guest.jobcenter_name ? (
                              <div className="flex items-center gap-2">
                                <Building2 className="h-3 w-3 text-slate-400" />
                                {guest.jobcenter_name}
                              </div>
                            ) : !isSozial && guest.arbeitgeber ? (
                              <div className="flex items-center gap-2">
                                <Briefcase className="h-3 w-3 text-slate-400" />
                                {guest.arbeitgeber}
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {isSozial && guest.leistung_end_date ? (
                            <div 
                              className="flex items-center gap-2 cursor-pointer hover:underline"
                              onClick={() => openLeistungDetail(guest)}
                            >
                              {leistungStatus?.status === "critical" && (
                                <AlertTriangle className="h-4 w-4 text-red-500" />
                              )}
                              <span className={leistungStatus?.status === "critical" ? "text-red-600 font-medium" : ""}>
                                {format(parseISO(guest.leistung_end_date), "dd.MM.yyyy", { locale: de })}
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {guest.current_bed_id ? (
                              <Badge 
                                className="status-occupied cursor-pointer hover:opacity-80"
                                onClick={() => openRoomDetail(guest)}
                              >
                                <Bed className="h-3 w-3 mr-1" />
                                {roomInfo?.room?.room_number || 'Untergebracht'}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-slate-600">Nicht untergebracht</Badge>
                            )}
                            {isSozial && leistungStatus && (
                              <Badge 
                                className={`${leistungStatus.class} cursor-pointer hover:opacity-80`}
                                onClick={() => openLeistungDetail(guest)}
                              >
                                {leistungStatus.label}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDocumentDialog(guest)}
                              title="Dokumente"
                              data-testid={`docs-guest-${guest.id}`}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(guest)}
                              data-testid={`edit-guest-${guest.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {showArchived ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => handleRestore(guest.id)}
                                  title="Wiederherstellen"
                                  data-testid={`restore-guest-${guest.id}`}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDelete(guest.id)}
                                  title="Endgültig löschen"
                                  data-testid={`delete-guest-${guest.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                {user?.role === "admin" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => openBlacklistDialog(guest)}
                                    title="Zur Blacklist"
                                    data-testid={`blacklist-guest-${guest.id}`}
                                  >
                                    <Ban className="h-4 w-4" />
                                  </Button>
                                )}
                                {guest.email && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    onClick={() => openEmailDialog(guest)}
                                    title="E-Mail senden"
                                    data-testid={`email-guest-${guest.id}`}
                                  >
                                    <Send className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  onClick={() => openArchiveDialog(guest)}
                                  title="Archivieren"
                                  data-testid={`archive-guest-${guest.id}`}
                                >
                                  <Archive className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Keine Bewohner gefunden
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Document Dialog */}
      <Dialog open={documentDialogOpen} onOpenChange={setDocumentDialogOpen}>
        <DialogContent className="max-w-2xl mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dokumente - {selectedGuestForDocs?.first_name} {selectedGuestForDocs?.last_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Upload Section */}
            <Card className="border-slate-200 bg-slate-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Neues Dokument hochladen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Dokumenttyp</Label>
                    <Select
                      value={docFormData.document_type}
                      onValueChange={(value) => setDocFormData((prev) => ({ ...prev, document_type: value }))}
                    >
                      <SelectTrigger data-testid="doc-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kimlik">Ausweis/Pass</SelectItem>
                        <SelectItem value="leistungsbescheid">Leistungsbescheid</SelectItem>
                        <SelectItem value="mietvertrag">Mietvertrag</SelectItem>
                        <SelectItem value="sonstige">Sonstige</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="doc_title">Titel</Label>
                    <Input
                      id="doc_title"
                      value={docFormData.title}
                      onChange={(e) => setDocFormData((prev) => ({ ...prev, title: e.target.value }))}
                      placeholder="z.B. Personalausweis"
                      data-testid="doc-title-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="doc_description">Beschreibung (optional)</Label>
                  <Input
                    id="doc_description"
                    value={docFormData.description}
                    onChange={(e) => setDocFormData((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Zusätzliche Informationen..."
                    data-testid="doc-description-input"
                  />
                </div>
                <div className="pt-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    data-testid="doc-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingDoc}
                    className="w-full"
                    data-testid="doc-upload-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadingDoc ? "Wird hochgeladen..." : "Datei auswählen und hochladen"}
                  </Button>
                  <p className="text-xs text-slate-500 mt-2">
                    Unterstützte Formate: PDF, JPG, PNG, DOC, DOCX (max. 10MB)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Documents List */}
            <div className="space-y-3">
              <h4 className="font-medium text-slate-700">
                Vorhandene Dokumente ({documents.length})
              </h4>
              {documents.length > 0 ? (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
                      data-testid={`document-${doc.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          <File className="h-5 w-5 text-slate-500" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{doc.title}</div>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Badge className={`text-xs ${getDocumentTypeColor(doc.document_type)}`}>
                              {getDocumentTypeLabel(doc.document_type)}
                            </Badge>
                            <span>•</span>
                            <span>{doc.file_name}</span>
                            <span>•</span>
                            <span>{format(new Date(doc.uploaded_at), "dd.MM.yyyy", { locale: de })}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadDocument(doc.id, doc.file_name)}
                          title="Herunterladen"
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteDocument(doc.id)}
                          title="Löschen"
                          data-testid={`delete-doc-${doc.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                  <FileText className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  <p>Keine Dokumente vorhanden</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Room Detail Dialog */}
      <Dialog open={roomDetailOpen} onOpenChange={setRoomDetailOpen}>
        <DialogContent className="max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Bed className="h-5 w-5" />
              Unterkunft Details
            </DialogTitle>
          </DialogHeader>
          {selectedGuestForRoom && (() => {
            const roomInfo = getGuestRoomInfo(selectedGuestForRoom);
            return (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-700 mb-3">Bewohner</h4>
                  <p className="font-medium text-lg">
                    {selectedGuestForRoom.first_name} {selectedGuestForRoom.last_name}
                  </p>
                  {selectedGuestForRoom.aktenzeichen && (
                    <p className="text-sm text-slate-500 font-mono">{selectedGuestForRoom.aktenzeichen}</p>
                  )}
                </div>
                
                {roomInfo?.room && (
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <h4 className="font-medium text-emerald-800 mb-3">Zimmer</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-emerald-600">Zimmernummer</p>
                        <p className="font-medium text-lg text-emerald-900">{roomInfo.room.room_number}</p>
                      </div>
                      <div>
                        <p className="text-xs text-emerald-600">Etage</p>
                        <p className="font-medium text-emerald-900">{roomInfo.room.floor || '-'}</p>
                      </div>
                      {roomInfo.bed && (
                        <div className="col-span-2">
                          <p className="text-xs text-emerald-600">Bett</p>
                          <p className="font-medium text-emerald-900">{roomInfo.bed.bed_number}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => setRoomDetailOpen(false)}
                >
                  Schließen
                </Button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Leistung Detail Dialog */}
      <Dialog open={leistungDetailOpen} onOpenChange={setLeistungDetailOpen}>
        <DialogContent className="max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Calendar className="h-5 w-5" />
              Leistungsübernahme Details
            </DialogTitle>
          </DialogHeader>
          {selectedGuestForLeistung && (() => {
            const leistungStatus = getLeistungStatus(selectedGuestForLeistung.leistung_end_date);
            return (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-700 mb-3">Bewohner</h4>
                  <p className="font-medium text-lg">
                    {selectedGuestForLeistung.first_name} {selectedGuestForLeistung.last_name}
                  </p>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-3">Jobcenter</h4>
                  <div className="space-y-2">
                    {selectedGuestForLeistung.jobcenter_name && (
                      <div>
                        <p className="text-xs text-blue-600">Jobcenter</p>
                        <p className="font-medium text-blue-900">{selectedGuestForLeistung.jobcenter_name}</p>
                      </div>
                    )}
                    {selectedGuestForLeistung.aktenzeichen && (
                      <div>
                        <p className="text-xs text-blue-600">Aktenzeichen</p>
                        <p className="font-medium font-mono text-blue-900">{selectedGuestForLeistung.aktenzeichen}</p>
                      </div>
                    )}
                    {selectedGuestForLeistung.sachbearbeiter_name && (
                      <div>
                        <p className="text-xs text-blue-600">Sachbearbeiter</p>
                        <p className="font-medium text-blue-900">
                          {selectedGuestForLeistung.sachbearbeiter_name}
                          {selectedGuestForLeistung.sachbearbeiter_phone && ` - ${selectedGuestForLeistung.sachbearbeiter_phone}`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className={`p-4 rounded-lg border ${
                  leistungStatus?.status === 'critical' ? 'bg-red-50 border-red-200' :
                  leistungStatus?.status === 'warning' ? 'bg-amber-50 border-amber-200' :
                  'bg-emerald-50 border-emerald-200'
                }`}>
                  <h4 className={`font-medium mb-3 ${
                    leistungStatus?.status === 'critical' ? 'text-red-800' :
                    leistungStatus?.status === 'warning' ? 'text-amber-800' :
                    'text-emerald-800'
                  }`}>Leistungszeitraum</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs opacity-70">Beginn</p>
                      <p className="font-medium">
                        {selectedGuestForLeistung.leistung_start_date 
                          ? format(parseISO(selectedGuestForLeistung.leistung_start_date), "dd.MM.yyyy", { locale: de })
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs opacity-70">Ende</p>
                      <p className="font-medium">
                        {selectedGuestForLeistung.leistung_end_date 
                          ? format(parseISO(selectedGuestForLeistung.leistung_end_date), "dd.MM.yyyy", { locale: de })
                          : '-'}
                      </p>
                    </div>
                    {leistungStatus && (
                      <div className="col-span-2 pt-2 border-t border-current/20">
                        <p className="text-xs opacity-70">Status</p>
                        <p className="font-medium text-lg flex items-center gap-2">
                          {leistungStatus.status === 'critical' && <AlertTriangle className="h-5 w-5" />}
                          {leistungStatus.label}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1" 
                    onClick={() => setLeistungDetailOpen(false)}
                  >
                    Schließen
                  </Button>
                  <Button 
                    className="flex-1 bg-slate-900 hover:bg-slate-800" 
                    onClick={() => { setLeistungDetailOpen(false); handleEdit(selectedGuestForLeistung); }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Bearbeiten
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bewohner archivieren?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedGuestForArchive && (
                <>
                  <strong>{selectedGuestForArchive.first_name} {selectedGuestForArchive.last_name}</strong> wird in das Archiv verschoben. 
                  Sie können den Bewohner später wiederherstellen oder endgültig löschen.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleArchive}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Archive className="h-4 w-4 mr-2" />
              Archivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Blacklist Dialog */}
      <Dialog open={blacklistDialogOpen} onOpenChange={setBlacklistDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="h-5 w-5" />
              Zur Blacklist hinzufügen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedGuestForBlacklist && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium">
                  {selectedGuestForBlacklist.first_name} {selectedGuestForBlacklist.last_name}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Grund *</Label>
              <Input
                value={blacklistReason}
                onChange={(e) => setBlacklistReason(e.target.value)}
                placeholder="z.B. Mietschulden, Sachbeschädigung..."
                data-testid="blacklist-reason-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notizen</Label>
              <Textarea
                value={blacklistNotes}
                onChange={(e) => setBlacklistNotes(e.target.value)}
                placeholder="Zusätzliche Informationen..."
                data-testid="blacklist-notes-input"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setBlacklistDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleAddToBlacklist}
                disabled={addingToBlacklist || !blacklistReason}
                data-testid="confirm-blacklist-btn"
              >
                {addingToBlacklist ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ban className="h-4 w-4 mr-2" />}
                {addingToBlacklist ? "Wird hinzugefügt..." : "Hinzufügen"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" style={{ color: "#c11718" }} />
              E-Mail senden
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedGuestForEmail && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium">
                  {selectedGuestForEmail.first_name} {selectedGuestForEmail.last_name}
                </p>
                <p className="text-xs text-slate-500">{selectedGuestForEmail.email}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>E-Mail-Typ</Label>
              <Select value={emailType} onValueChange={(val) => { setEmailType(val); setPreviewPdf(null); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice">Rechnung</SelectItem>
                  <SelectItem value="reminder1">1. Zahlungserinnerung</SelectItem>
                  <SelectItem value="reminder2">2. Mahnung</SelectItem>
                  <SelectItem value="reminder3">3. Mahnung (Letzte)</SelectItem>
                  <SelectItem value="statement">Kontoauszug</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Ay/Yıl seçimi - sadece Rechnung için */}
            {/* Ay/Yıl seçimi - tüm türler için */}
            <div className="space-y-2">
              <Label>Abrechnungszeitraum</Label>
              <div className="flex gap-2">
                <Select value={emailMonth.toString()} onValueChange={(val) => { setEmailMonth(parseInt(val)); setPreviewPdf(null); }}>
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Januar</SelectItem>
                    <SelectItem value="2">Februar</SelectItem>
                    <SelectItem value="3">März</SelectItem>
                    <SelectItem value="4">April</SelectItem>
                    <SelectItem value="5">Mai</SelectItem>
                    <SelectItem value="6">Juni</SelectItem>
                    <SelectItem value="7">Juli</SelectItem>
                    <SelectItem value="8">August</SelectItem>
                    <SelectItem value="9">September</SelectItem>
                    <SelectItem value="10">Oktober</SelectItem>
                    <SelectItem value="11">November</SelectItem>
                    <SelectItem value="12">Dezember</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={emailYear.toString()} onValueChange={(val) => { setEmailYear(parseInt(val)); setPreviewPdf(null); }}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() - 2 + i;
                      return <SelectItem key={year} value={year.toString()}>{year}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Önizleme butonu - tüm türler için */}
            <Button 
              variant="outline" 
              className="w-full"
              onClick={handlePreviewInvoice}
              disabled={previewLoading}
              data-testid="preview-invoice-btn"
            >
              {previewLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
              {previewLoading ? "Wird geladen..." : "Vorschau anzeigen"}
            </Button>
            
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => { setEmailDialogOpen(false); setPreviewPdf(null); }}>
                Abbrechen
              </Button>
              <Button 
                className="flex-1"
                style={{ backgroundColor: "#c11718" }}
                onClick={handleSendEmail}
                disabled={sendingEmail}
                data-testid="send-email-btn"
              >
                {sendingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                {sendingEmail ? "Wird gesendet..." : "Senden"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" style={{ color: "#c11718" }} />
              Rechnungsvorschau
            </DialogTitle>
          </DialogHeader>
          <div className="h-[70vh]">
            {previewPdf && (
              <iframe 
                src={`data:application/pdf;base64,${previewPdf}`}
                className="w-full h-full border rounded-lg"
                title="Rechnungsvorschau"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
